import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import api from '../../services/api';

const StatusManagementPage = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [statusUsage, setStatusUsage] = useState({});
  const [formData, setFormData] = useState({
    status_id: '',
    value: '',
    is_active: true
  });



  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/exams/statuses/');
      
      // Ensure we always have an array, even if the API returns something unexpected
      let statusesData = [];
      if (response.data && Array.isArray(response.data)) {
        statusesData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Handle paginated responses
        statusesData = response.data.results;
      } else {
        statusesData = [];
      }
      
      setStatuses(statusesData);
      
      // Check usage for each status
      if (statusesData.length > 0) {
        await checkStatusUsage(statusesData);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error fetching statuses',
        severity: 'error'
      });
      setStatuses([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const checkStatusUsage = async (statusesList) => {
    const usageData = {};
    
    for (const status of statusesList) {
      try {
        const usageResponse = await api.get(`/exams/statuses/${status.id}/usage_check/`);
        usageData[status.id] = usageResponse.data;
      } catch (error) {
        usageData[status.id] = { is_used: false, usage_details: { questions: 0, tests: 0 } };
      }
    }
    
    setStatusUsage(usageData);
  };

  const handleAddNew = () => {
    setEditingStatus(null);
    setFormData({ status_id: '', value: '', is_active: true });
    setDialogOpen(true);
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormData({
      status_id: status.status_id,
      value: status.value,
      is_active: status.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    const usage = statusUsage[statusId];
    
    if (usage?.is_used) {
      setSnackbar({
        open: true,
        message: `Cannot delete status "${status?.status_id}": It is being used in ${usage.usage_details.questions} questions and ${usage.usage_details.tests} tests`,
        severity: 'warning'
      });
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete the status "${status?.status_id}"?`)) {
      try {
        await api.delete(`/exams/statuses/${statusId}/`);
        setSnackbar({
          open: true,
          message: 'Status deleted successfully',
          severity: 'success'
        });
        fetchStatuses();
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error deleting status',
          severity: 'error'
        });
      }
    }
  };

  const handleSave = async () => {
    try {
      if (editingStatus) {
        // Update existing status
        await api.put(`/exams/statuses/${editingStatus.id}/`, formData);
        setSnackbar({
          open: true,
          message: 'Status updated successfully',
          severity: 'success'
        });
      } else {
        // Create new status
        await api.post('/exams/statuses/', formData);
        setSnackbar({
          open: true,
          message: 'Status created successfully',
          severity: 'success'
        });
      }
      setDialogOpen(false);
      fetchStatuses();
          } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error saving status',
          severity: 'error'
        });
      }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStatus(null);
    setFormData({ status_id: '', value: '', is_active: true });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AdminLayoutWrapper>
      <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Status Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New Status
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
                                     <TableHead>
              <TableRow>
                <TableCell>Status ID</TableCell>
                <TableCell>Display Value</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
                         <TableBody>
               {Array.isArray(statuses) && statuses.length > 0 ? (
                 statuses.map((status) => (
                   <TableRow key={status.id} hover>
                                        <TableCell>
                     <Chip 
                       label={status.status_id} 
                       color="primary" 
                       variant="outlined"
                       size="small"
                     />
                   </TableCell>
                     <TableCell>{status.value}</TableCell>
                     <TableCell>
                       <Chip 
                         label={status.is_active ? 'Active' : 'Inactive'} 
                         color={status.is_active ? 'success' : 'default'} 
                         variant="outlined"
                         size="small"
                       />
                     </TableCell>
                     <TableCell>
                       {statusUsage[status.id] ? (
                         statusUsage[status.id].is_used ? (
                           <Chip 
                             label={`${statusUsage[status.id].usage_details.questions || 0} questions, ${statusUsage[status.id].usage_details.tests || 0} tests`}
                             color="warning" 
                             variant="outlined"
                             size="small"
                           />
                         ) : (
                           <Chip 
                             label="Not used" 
                             color="success" 
                             variant="outlined"
                             size="small"
                           />
                         )
                       ) : (
                         <CircularProgress size={16} />
                       )}
                     </TableCell>
                     <TableCell>
                       {new Date(status.created_at).toLocaleDateString()}
                     </TableCell>
                     <TableCell>
                       {new Date(status.updated_at).toLocaleDateString()}
                     </TableCell>
                     <TableCell align="center">
                       <IconButton
                         color="primary"
                         onClick={() => handleEdit(status)}
                         size="small"
                       >
                         <EditIcon />
                       </IconButton>
                                               <IconButton
                          color="error"
                          onClick={() => handleDelete(status.id)}
                          size="small"
                          disabled={statusUsage[status.id]?.is_used}
                          title={statusUsage[status.id]?.is_used ? 
                            `Cannot delete: Used in ${statusUsage[status.id]?.usage_details?.questions || 0} questions and ${statusUsage[status.id]?.usage_details?.tests || 0} tests` : 
                            'Delete status'
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                     </TableCell>
                   </TableRow>
                 ))
               ) : (
                 <TableRow>
                   <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                     <Typography variant="body2" color="text.secondary">
                       {loading ? 'Loading statuses...' : 'No statuses found'}
                     </Typography>
                   </TableCell>
                 </TableRow>
               )}
             </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStatus ? 'Edit Status' : 'Add New Status'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
                         <TextField
               fullWidth
               label="Status ID"
               value={formData.status_id}
               onChange={(e) => setFormData({ ...formData, status_id: e.target.value })}
               disabled={!!editingStatus} // Can't change ID for existing status
               inputProps={{ maxLength: 10 }}
               helperText={`${formData.status_id.length}/10 characters`}
               sx={{ mb: 2 }}
             />
            <TextField
              fullWidth
              label="Display Value"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  color="primary"
                />
              }
              label="Active Status"
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={!formData.status_id || !formData.value}
          >
            {editingStatus ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </AdminLayoutWrapper>
  );
};

export default StatusManagementPage;
